import React from "react";
import { render, unmountComponentAtNode } from "react-dom";
import { Action } from "redux";
import { Task } from "redux-saga";
import { call, takeEvery } from "redux-saga/effects";
import { AppContext } from "App";

import {
    AggregationRow,
    AggregationRowKey,
    ColumnAggregationFunctions,
    ColumnDefinition,
    ColumnKey,
    ColumnType,
    createClearAggregationRowAction,
    createColumnAddedAction,
    createColumnDefinitionsChangedAction,
    createColumnRemovedAction,
    createColumnWidthChangedAction,
    createFilterChangedAction,
    createInitAggregationRowAction,
    createNumberOfFrozenColumnsChangedAction,
    createOrderOfColumnsChangedAction,
    createReloadAggregationResultsAction,
    Filter,
    ICloseButtonActivatedAction,
    unsafeGetAllColumnsAggregationFunctions,
    unsafeGetColumnsAggregationFunctions,
} from "@multiweb/aggregationRow";
import { bridge } from "@multiweb/reactCore/bridge";
import { createRootComponent, StoreManager } from "@multiweb/reactSetup";
import { OWDataType } from "@multiweb/types/contracts";
import { SagaWithReturnType } from "@multiweb/types/react";
import { Event } from "Global/Base/Events";
import { IDisposable } from "Global/Base/IDisposable";
import { Column } from "UI/UI.Controls/DataGridSubcontrols/Columns/Column";

const { dispatchAction, runSaga, getState } = bridge;

export type OnCloseCallback = () => void;
export interface IColumnWidthChangedEventData {readonly columnKey: ColumnKey; readonly width: number}
export interface INumberOfFrozenColumnsChangedEventData {readonly numberOfFrozenColumns: number}
export interface IOrderOfColumnsChangedEventData {readonly orderOfColumns: ReadonlyArray<ColumnKey>}
export interface IColumnAddedEventData {readonly column: ColumnDefinition}
export interface IColumnRemovedEventData {readonly columnKey: ColumnKey}
export interface IFilterChangedEventData {readonly filter: Filter}
export interface IColumnDefinitionsChangedEventData {
    readonly columnDefinitions: ReadonlyArray<ColumnDefinition>;
    readonly orderOfColumns: ReadonlyArray<ColumnKey>;
    readonly numberOfFrozenColumns: number;
}

export interface IAggregationRowEvents {
    /** Událost pro zaregistrování posluchače pro změnu šířky sloupců. */
    readonly columnWidthChangedEvent?: Event<any, IColumnWidthChangedEventData>;
    /** Událost pro zaregistrování posluchače na změnu zmražených sloupců. */
    readonly numberOfFrozenColumnsChangedEvent?: Event<any, INumberOfFrozenColumnsChangedEventData>;
    /** Událost pro zaregistrování posluchače na změnu pořadí sloupců. */
    readonly orderOfColumnsChangedEvent?: Event<any, IOrderOfColumnsChangedEventData>;
    /** Událost pro zaregistrování posluchače na změnu definic sloupců. */
    readonly columnDefinitionsChangedEvent?: Event<any, IColumnDefinitionsChangedEventData>;
    /** Událost pro zaregistrování posluchače na přidání sloupce. */
    readonly columnAddedEvent?: Event<any, IColumnAddedEventData>;
    /** Událost pro zaregistrování posluchače na odebrání sloupce. */
    readonly columnRemovedEvent?: Event<any, IColumnRemovedEventData>;
    /** Událost pro zaregistrování posluchače na změnu filtru. */
    readonly filterChangedEvent?: Event<any, IFilterChangedEventData>;
    /** Událost pro zaregistrování posluchače na událost aktualizace záznamů. */
    readonly refreshEvent?: Event<any, void>;
}

type Listener<EventData> = (sender: any, eventData: EventData) => void;

/**
 * Slouží k propojení stávající aplikace a modulu agregačního řádku.
 */
export class AggregationRowConnector implements IDisposable {
    private readonly _rootElement: Element;
    private readonly _aggregationRowKey: AggregationRowKey;
    private readonly _sagaTask: Task;
    private _columnWidthChangedEvent?: Event<any, IColumnWidthChangedEventData>;
    private _columnWidthChangedListener?: Listener<IColumnWidthChangedEventData>;
    private _numberOfFrozenColumnsChangedEvent?: Event<any, INumberOfFrozenColumnsChangedEventData>;
    private _numberOfFrozenColumnsChangedListener?: Listener<INumberOfFrozenColumnsChangedEventData>;
    private _orderOfColumnsChangedEvent?: Event<any, IOrderOfColumnsChangedEventData>;
    private _orderOfColumnsChangedListener?: Listener<IOrderOfColumnsChangedEventData>;
    private _columnDefinitionsChangedEvent?: Event<any, IColumnDefinitionsChangedEventData>;
    private _columnDefinitionsChangedListener?: Listener<IColumnDefinitionsChangedEventData>;
    private _columnAddedEvent?: Event<any, IColumnAddedEventData>;
    private _columnAddedListener?: Listener<IColumnAddedEventData>;
    private _columnRemovedEvent?: Event<any, IColumnRemovedEventData>;
    private _columnRemovedListener?: Listener<IColumnRemovedEventData>;
    private _filterChangedEvent?: Event<any, IFilterChangedEventData>;
    private _filterChangedListener?: Listener<IFilterChangedEventData>;
    private _refreshEvent?: Event<any, void>;
    private _refreshListener?: Listener<void>;

    /**
     * Převede datový typ sloupce z původní aplikace na typ sloupce pro agregační řádek.
     * @param dataType
     */
    private static _dataTypeToColumnType = (dataType: OWDataType | string): ColumnType => {
        switch (dataType) {
            case OWDataType.byte:
            case OWDataType.short:
            case OWDataType.integer:
            case OWDataType.single:
            case OWDataType.double:
            case OWDataType.bcd:
            case OWDataType.money:
            case OWDataType.decimal:
            case OWDataType.int64:
                return ColumnType.numeric;
            default:
                return ColumnType.other;
        }
    };

    /**
     * Šířka vertikálního oddělovače v pixelech.
     */
    private static readonly _VERTICAL_DIVIDER_WIDTH = 1;

    /**
     * Vytvoří definici sloupce pro agregační řádek na základě sloupce z původní aplikace.
     * @param column Sloupec, ze kterého se vytvoří definice.
     * @param forceOtherDataType Pokud _true_, pak se jako typ sloupce nastaví {@link ColumnType.other}. Bez ohledu na cokoli dalšího.
     * (Používá se např. pro sloupce s povoleným šifrováním. Pro ty agregace nemá smysl.)
     */
    public static columnToColumnDefinition = (column: Column, forceOtherDataType: boolean): ColumnDefinition => ({
        key: column.columnId,
        attributeName: column.attrName,
        width: column.width - AggregationRowConnector._VERTICAL_DIVIDER_WIDTH, // šířky sloupců v sobě mají zahrnutý i border jako oddělovač, agregační řádek má oddělovač zvlášť
        columnType: (forceOtherDataType ? ColumnType.other : AggregationRowConnector._dataTypeToColumnType(column.dataType)),
        format: column.displayFormat,
    });

    /**
     * Vytvoří Connector pro konkrétní agregační řádek.
     * @param element Html element, do kterého se vloží agregační řádek.
     * @param aggregationRowKey Klíč pro jednoznačnou identifikaci agregačního řádku.
     * @param className Jméno třídy, jejíž záznamy se agregují.
     * @param columnDefinitions Definice sloupců. Součástí definice musí být i případný sloupec s ovládacími prvky.
     * @param orderOfColumns Pořadí sloupců zadané pomocí jejich klíčů.
     * @param numberOfFrozenColumns Počet sloupců zmražených vůči horizontálnímu scrollování.
     * @param filter Aktuálně nastavený filtr pro záznamy třídy.
     * @param onClose Callback volaný při aktivaci tlačítka pro zavření agregačního řádku. Pokud využívá this, musí být již bindovaný, protože se volá pomocí ságy.
     * @param events Objekt obsahující události, na které může agregační řádek reagovat.
     */
    constructor(
        element: HTMLElement,
        aggregationRowKey: AggregationRowKey,
        className: string,
        columnDefinitions: ReadonlyArray<ColumnDefinition>,
        orderOfColumns: ReadonlyArray<ColumnKey>,
        numberOfFrozenColumns: number,
        filter: Filter,
        onClose: OnCloseCallback,
        events: IAggregationRowEvents,
    ) {
        this._rootElement = element;
        this._aggregationRowKey = aggregationRowKey;

        const isDev = !!AppContext.instance.configuration.developmentMode;
        StoreManager.initializeStore(isDev);
        const RootComponent = createRootComponent(isDev);
        dispatchAction(createInitAggregationRowAction(aggregationRowKey, className, columnDefinitions, orderOfColumns, numberOfFrozenColumns, filter));
        this._sagaTask = runSaga(aggregationRowConnectorSaga, aggregationRowKey, onClose);
        this.initializeListeners(aggregationRowKey, events);

        render(
            <RootComponent>
                <AggregationRow aggregationRowKey={aggregationRowKey} />
            </RootComponent>,
            this._rootElement,
        );
    }

    private initializeListeners(
        aggregationRowKey: AggregationRowKey,
        {
            columnWidthChangedEvent,
            numberOfFrozenColumnsChangedEvent,
            orderOfColumnsChangedEvent,
            columnDefinitionsChangedEvent,
            columnAddedEvent,
            columnRemovedEvent,
            filterChangedEvent,
            refreshEvent,
        }: IAggregationRowEvents,
    ): void {
        if (columnWidthChangedEvent) {
            this._columnWidthChangedEvent = columnWidthChangedEvent;
            this._columnWidthChangedListener = (_sender, { columnKey, width }) => dispatchAction(createColumnWidthChangedAction(aggregationRowKey, columnKey, width));
            this._columnWidthChangedEvent.add(this._columnWidthChangedListener);
        }
        if (numberOfFrozenColumnsChangedEvent) {
            this._numberOfFrozenColumnsChangedEvent = numberOfFrozenColumnsChangedEvent;
            this._numberOfFrozenColumnsChangedListener = (_sender, { numberOfFrozenColumns }) => dispatchAction(createNumberOfFrozenColumnsChangedAction(aggregationRowKey, numberOfFrozenColumns));
            this._numberOfFrozenColumnsChangedEvent.add(this._numberOfFrozenColumnsChangedListener);
        }
        if (orderOfColumnsChangedEvent) {
            this._orderOfColumnsChangedEvent = orderOfColumnsChangedEvent;
            this._orderOfColumnsChangedListener = (_sender, { orderOfColumns }) => dispatchAction(createOrderOfColumnsChangedAction(aggregationRowKey, orderOfColumns));
            this._orderOfColumnsChangedEvent.add(this._orderOfColumnsChangedListener);
        }
        if (columnDefinitionsChangedEvent) {
            this._columnDefinitionsChangedEvent = columnDefinitionsChangedEvent;
            this._columnDefinitionsChangedListener = (_sender, { columnDefinitions, orderOfColumns, numberOfFrozenColumns }) => (
                dispatchAction(createColumnDefinitionsChangedAction(this._aggregationRowKey, columnDefinitions, orderOfColumns, numberOfFrozenColumns))
            );
            this._columnDefinitionsChangedEvent.add(this._columnDefinitionsChangedListener);
        }
        if (columnAddedEvent) {
            this._columnAddedEvent = columnAddedEvent;
            this._columnAddedListener = (_sender, { column }) => {
                const aggregationColumns = this.getColumnsAggregationFunctions(false);
                if (aggregationColumns[column.key] === undefined)
                    dispatchAction(createColumnAddedAction(aggregationRowKey, column));
            };
            this._columnAddedEvent.add(this._columnAddedListener);
        }
        if (columnRemovedEvent) {
            this._columnRemovedEvent = columnRemovedEvent;
            this._columnRemovedListener = (_sender, { columnKey }) => dispatchAction(createColumnRemovedAction(aggregationRowKey, columnKey));
            this._columnRemovedEvent.add(this._columnRemovedListener);
        }
        if (filterChangedEvent) {
            this._filterChangedEvent = filterChangedEvent;
            this._filterChangedListener = (_sender, { filter }) => dispatchAction(createFilterChangedAction(aggregationRowKey, filter));
            this._filterChangedEvent.add(this._filterChangedListener);
        }
        if (refreshEvent) {
            this._refreshEvent = refreshEvent;
            this._refreshListener = () => dispatchAction(createReloadAggregationResultsAction(aggregationRowKey));
            this._refreshEvent.add(this._refreshListener);
        }
    }

    private removeListeners(): void {
        if (this._columnWidthChangedEvent && this._columnWidthChangedListener) {
            this._columnWidthChangedEvent.remove(this._columnWidthChangedListener);
        }
        if (this._numberOfFrozenColumnsChangedEvent && this._numberOfFrozenColumnsChangedListener) {
            this._numberOfFrozenColumnsChangedEvent.remove(this._numberOfFrozenColumnsChangedListener);
        }
        if (this._orderOfColumnsChangedEvent && this._orderOfColumnsChangedListener) {
            this._orderOfColumnsChangedEvent.remove(this._orderOfColumnsChangedListener);
        }
        if (this._columnDefinitionsChangedEvent && this._columnDefinitionsChangedListener) {
            this._columnDefinitionsChangedEvent.remove(this._columnDefinitionsChangedListener);
        }
        if (this._columnAddedEvent && this._columnAddedListener) {
            this._columnAddedEvent.remove(this._columnAddedListener);
        }
        if (this._columnRemovedEvent && this._columnRemovedListener) {
            this._columnRemovedEvent.remove(this._columnRemovedListener);
        }
        if (this._filterChangedEvent && this._filterChangedListener) {
            this._filterChangedEvent.remove(this._filterChangedListener);
        }
        if (this._refreshEvent && this._refreshListener) {
            this._refreshEvent.remove(this._refreshListener);
        }
    }

    /**
     * Získá objekt obsahující zvolené agregační funkce pod klíči sloupců agregačního řádku.
     * V případě že není zvolena žádná funkce je pod klíčem hodnota null.
     * @param getOnlyNumericColumns Příznak, zda má vrátit pouze numerické sloupce, jinak vrátí všechny
     */
    public getColumnsAggregationFunctions(getOnlyNumericColumns: boolean = true): ColumnAggregationFunctions {
        if (getOnlyNumericColumns)
            return unsafeGetColumnsAggregationFunctions(getState(), { aggregationRowKey: this._aggregationRowKey });
        return unsafeGetAllColumnsAggregationFunctions(getState(), { aggregationRowKey: this._aggregationRowKey });
    }

    public dispose(): void {
        unmountComponentAtNode(this._rootElement);
        dispatchAction(createClearAggregationRowAction(this._aggregationRowKey));
        this._sagaTask.cancel();
        this.removeListeners();
    }
}

/**
 * Hlavní sága pro AggregationRowConnector. Poslouchá na relevantní akce z reduxu a zařizuje jejich obsluhu.
 * @param aggregationRowKey Klíč agregačního řádku, ke kterému Connector patří.
 * @param onClose Callback volaný při zavření agregačního řádku.
 */
const aggregationRowConnectorSaga: SagaWithReturnType<[AggregationRowKey, OnCloseCallback]> = function* aggregationRowConnectorSaga(aggregationRowKey, onClose) {
    yield takeEvery((action: Action) => action instanceof ICloseButtonActivatedAction && action.meta.aggregationRowKey === aggregationRowKey, closeButtonActivatedSaga, onClose);
};

/**
 * Obsluhuje aktivaci tlačítka pro zavření agregačního řádku.
 * @param onClose Callback, který se při zavření zavolá.
 */
const closeButtonActivatedSaga: SagaWithReturnType<[OnCloseCallback]> = function* closeButtonActivatedSaga(onClose) {
    yield call(onClose);
};
